import Component from '@glimmer/component';
import { action, computed, set } from '@ember/object';
import { inject as service } from '@ember/service';
import { reads } from '@ember/object/computed';
import { tracked } from '@glimmer/tracking';
import { A } from '@ember/array';

import PS from 'mewe/utils/pubsub';
import { toTeller, fetchEntriesByTeller } from 'mewe/fetchers/fetch-stories';
import { TellerType } from 'mewe/constants';
import dispatcher from 'mewe/dispatcher';
import { ds } from 'mewe/stores/ds';
import FunctionalUtils from 'mewe/shared/functional-utils';

export default class MwStoriesList extends Component {
  @service dynamicDialogs;
  @service account;
  @service tasks;

  @reads('args.myEntries') myEntries;
  @reads('args.tellers') tellers;

  @tracked storiesCount = 0;
  @tracked lastOrderedAt = 0;

  options = {
    mandatory: ['tellers'],
    optional: ['filter', 'myEntries'],
  };

  constructor() {
    super(...arguments);

    this.wsNewStoryBind = this.wsNewStory.bind(this);
    PS.Sub('new.story', this.wsNewStoryBind);
  }

  willDestroy() {
    PS.Unsub('new.story', this.wsNewStoryBind);
  }

  @computed('myEntries.{items.length,hasNewStories,isFetching}')
  get currentUserHaveNewEntries() {
    if (this.myEntries?.isFetching) return false;
    return this.myEntries?.hasNewStories || this.myEntries?.items?.filterBy('isNew', true).length > 0;
  }

  @computed('tellers.items.length', 'args.filter', 'lastOrderedAt')
  get tellersFiltered() {
    return this.getTellers();
  }

  getTellers(unseenOnly) {
    let tellers = this.tellers.items;

    // filtering depends on location: all stories on Home view, Page stories on Pages view
    if (this.args.filter) {
      tellers = A(tellers.filterBy('storytellerType', this.args.filter));
    }

    // return only tellers with unseen stories
    if (unseenOnly) {
      tellers = A(tellers.filterBy('hasNewStories', true));
    }

    // general sorting logic:
    // - followed tellers sorted by hasNewStories field, then non-followd
    //   tellers sorted by hasNewStories field.
    // - re-sorting should happen after closing stories-dialog because some tellers
    //   could have been followed in the dialog. Updating lastOrderedAt will trigger re-sorting.
    tellers = A(tellers.sort((a, b) => {
      if (a.isFollowing && !b.isFollowing) return -1;
      if (!a.isFollowing && b.isFollowing) return 1;
      return b.hasNewStories - a.hasNewStories;
    }));

    // need to flag first unfollowed teller to show divider in the UI,
    // but if there is no followed teller then we don't need to show the divider
    const firstFollowedTeller = tellers.find((t) => t.isFollowing);
    const firstUnfollowedTeller = tellers.find((t) => !t.isFollowing);
    if (firstUnfollowedTeller && firstFollowedTeller) {
      firstUnfollowedTeller.isFirstUnfollowed = true;
    }

    return tellers;
  }

  async wsNewStory(data) {
    if (data.storytellerId === this.account.activeUser.id) {
      fetchEntriesByTeller({ id: this.account.activeUser.id });
      set(this, 'myEntries.hasNewStories', true);
      return;
    }

    const tellers = ds.tellers.items;
    const existingTeller = tellers.find((t) => t.id === data.storytellerId);
    if (existingTeller) {
      existingTeller.setProperties({
        hasNewStories: true,
        storiesCount: existingTeller.stories.length + 1, // save the stories count for the analytics before cleaning stories array
        stories: A(),
      });
    } else {
      data.hasNewStories = true;
      let newTeller = toTeller(data);
      tellers.unshiftObject(newTeller);
      await fetchEntriesByTeller({ id: newTeller.id, tellerType: newTeller.storytellerType });
    }
  }

  @action
  updateMyEntries(entries) {
    this.myEntries.items = entries;
  }

  @action
  openCurrentUser() {
    if (!this.myEntries.items.length) {
      this.selectPhoto();
      return;
    }

    const currentUser = this.account.activeUser;
    const tellerData = toTeller({
      id: currentUser.id,
      storytellerType: TellerType.USER,
    });
    tellerData.userStoryteller = currentUser; // currentUser is EmberObject, can't be serialized

    const params = {
      tellerData,
      preloadedEntries: this.myEntries.items,
      onDeleteEntry: this.updateMyEntries,
    };

    this.dynamicDialogs.openDialog('stories-dialog', params);
  }

  @action
  open(teller) {
    const params = {
      tellerData: teller,
      tellersList: this.getTellers(teller.hasNewStories), // if opening unseen teller then filter out already seen tellers
      preloadedEntries: teller.stories,
      context: this.filter === 'Page' ? 'pages' : 'home',
      onClose: () => {
        this.lastOrderedAt = Date.now();
      },
    };

    this.dynamicDialogs.openDialog('stories-dialog', params);
  }

  @action
  selectPhoto(event) {
    event?.stopPropagation();

    const choosePhotoArgs = {
      url: '/api/v2/photo/st',
      task: this.tasks.getTask('photo.story'),
      useNewCropper: true,
    };
    this.dynamicDialogs.openDialog('choose-photo', choosePhotoArgs);
  }

  @action
  followTeller(teller, event) {
    event.stopPropagation();

    if (teller.storytellerType === 'Page') {
      dispatcher.dispatch('pages', 'toggleFollow', teller.pageStoryteller, {
        setToFollow: !teller.pageStoryteller.isFollower,
        doneCb: () => {
          FunctionalUtils.info(__('You are now following {name}', { name: teller.pageStoryteller.name }));
          set(teller, 'pageStoryteller.isFollower', true);
        },
      });
    } else {
      dispatcher.dispatch('contact', 'followUser',
        { user: teller.userStoryteller,
          doneCb: () => {
            set(teller, 'userRelation.following', true);
            this.lastOrderedAt = Date.now(); // trigger re-sorting of tellers
          },
        }
      );
    }
  }
}
